import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import ApiService from '../services/api';
import { Account, BulkCreateResponse, BatchStatus } from '../types';

const BulkUsers: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [results, setResults] = useState<BulkCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Batch processing state
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0]);
      }
    },
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await ApiService.getAccounts();
      setAccounts(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load accounts';
      setError(errorMessage);
    }
  };

  const startBatchPolling = (batchId: string) => {
    setIsPolling(true);
    
    const pollBatchStatus = async () => {
      try {
        const status = await ApiService.getBatchStatus(batchId);
        setBatchStatus(status);
        
        // Stop polling if batch is complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          stopBatchPolling();
          
          if (status.status === 'completed') {
            setSuccess(`Batch processing completed: ${status.successful_count} successful, ${status.failed_count} failed`);
          } else {
            setError(`Batch processing failed: ${status.error_message || 'Unknown error'}`);
          }
        }
      } catch (err: any) {
        console.error('Failed to poll batch status:', err);
        // Don't stop polling on individual errors - could be temporary
      }
    };
    
    // Poll immediately, then every 2 seconds
    pollBatchStatus();
    pollingIntervalRef.current = setInterval(pollBatchStatus, 2000);
  };

  const stopBatchPolling = () => {
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleFileUpload = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setError(`CSV parsing error: ${results.errors[0].message}`);
          return;
        }

        setCsvData(results.data as any[]);
        setCsvHeaders(results.meta.fields || []);
        setError(null);
        setSuccess(`Loaded ${results.data.length} users from CSV`);
      },
      error: (error) => {
        setError(`Failed to parse CSV: ${error.message}`);
      },
    });
  };

  const handleBulkCreate = async () => {
    if (!selectedAccount) {
      setError('Please select an account');
      return;
    }

    if (csvData.length === 0) {
      setError('Please upload a CSV file first');
      return;
    }

    // Validate required fields
    const requiredFields = ['username', 'email', 'name'];
    const missingFields = requiredFields.filter(field => !csvHeaders.includes(field));
    
    if (missingFields.length > 0) {
      setError(`CSV is missing required columns: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults(null);
      setBatchStatus(null);
      
      // Convert CSV data to the format expected by the API
      const csvContent = Papa.unparse(csvData);
      
      const result = await ApiService.bulkCreateUsers({
        account_id: selectedAccount,
        csv_content: csvContent,
        send_emails: true,
      });

      // Check if this is an async batch response
      if (result.batch_id && result.status === 'queued') {
        // Start polling for batch status
        setSuccess(`Large batch queued for processing. Tracking progress...`);
        startBatchPolling(result.batch_id);
      } else {
        // Immediate processing response
        setResults(result);
        setSuccess(`Bulk creation completed: ${result.summary.successful} successful, ${result.summary.failed} failed`);
      }
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = ApiService.generateCsvTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadResults = () => {
    if (!results) return;
    
    const csvContent = Papa.unparse(results.results);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk-create-results-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Bulk User Creation
      </Typography>

      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
        >
          Download Template
        </Button>
        
        {results && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadResults}
          >
            Download Results
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Select Target Account
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Account</InputLabel>
          <Select
            value={selectedAccount}
            label="Account"
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            {accounts.map((account) => (
              <MenuItem key={account.account_id} value={account.account_id}>
                {account.deployment_name} ({account.environment}) - {account.account_id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Upload CSV File
        </Typography>
        
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          {isDragActive ? (
            <Typography>Drop the CSV file here...</Typography>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Drag & drop a CSV file here, or click to select
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Required columns: username, email, name
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional columns: department, role, phone, etc.
              </Typography>
            </Box>
          )}
        </Box>

        {csvData.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>
              Preview ({csvData.length} users loaded)
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {csvHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.slice(0, 10).map((row, index) => (
                    <TableRow key={index}>
                      {csvHeaders.map((header) => (
                        <TableCell key={header}>
                          {row[header] || ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {csvData.length > 10 && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Showing first 10 rows. Total: {csvData.length} users
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {csvData.length > 0 && selectedAccount && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 3: Create Users
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleBulkCreate}
            disabled={loading || isPolling}
            size="large"
          >
            {loading ? 'Creating Users...' : isPolling ? 'Processing...' : `Create ${csvData.length} Users`}
          </Button>
        </Paper>
      )}

      {/* Batch Progress Display */}
      {batchStatus && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Batch Processing Progress
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body1">
              Processing {batchStatus.total_users} users
            </Typography>
            {isPolling && <CircularProgress size={20} />}
          </Box>
          
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2">
                Progress: {batchStatus.processed_count} / {batchStatus.total_users}
              </Typography>
              <Typography variant="body2">
                {batchStatus.progress_percentage.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={batchStatus.progress_percentage} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          
          <Box display="flex" gap={2}>
            <Chip 
              label={`Status: ${batchStatus.status}`}
              color={
                batchStatus.status === 'completed' ? 'success' :
                batchStatus.status === 'failed' ? 'error' :
                batchStatus.status === 'processing' ? 'warning' : 'default'
              }
            />
            <Chip 
              label={`Successful: ${batchStatus.successful_count}`} 
              color="success" 
              size="small"
            />
            <Chip 
              label={`Failed: ${batchStatus.failed_count}`} 
              color="error" 
              size="small"
            />
          </Box>
          
          {batchStatus.error_message && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {batchStatus.error_message}
            </Alert>
          )}
        </Paper>
      )}

      {results && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Results Summary
          </Typography>
          
          <Box display="flex" gap={2} mb={2}>
            <Chip 
              label={`Total: ${results.summary.total}`} 
              color="info" 
            />
            <Chip 
              label={`Successful: ${results.summary.successful}`} 
              color="success" 
            />
            <Chip 
              label={`Failed: ${results.summary.failed}`} 
              color="error" 
            />
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Password</TableCell>
                  <TableCell>Email Sent</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{result.username}</TableCell>
                    <TableCell>{result.email}</TableCell>
                    <TableCell>{result.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={result.status}
                        color={result.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.password ? (
                        <Typography variant="body2" fontFamily="monospace">
                          {result.password}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {result.email_sent !== undefined ? (
                        <Chip
                          label={result.email_sent ? 'Yes' : 'No'}
                          color={result.email_sent ? 'success' : 'warning'}
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {result.error && (
                        <Typography variant="body2" color="error">
                          {result.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {results.results.length} results shown
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={downloadResults}
            >
              Download full results as CSV
            </Link>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default BulkUsers;